import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import StraightenIcon from '@mui/icons-material/Straighten'
import Stack from '@mui/material/Stack'

// @mui imports
import { type SxProps } from '@mui/system'

// KN imports
import KNAttribute from 'components/KNAttribute/KNAttribute'
import { KNVolumeIcon, KNWeightIcon } from 'components/KNIcon/KNMaterialIcon'
import { GuestUserContext } from 'context/authentication/GuestUserContext'
import {
  getVolumeMeasurement,
  getVolumeUnit,
  getWeightMeasurement,
  getWeightUnit,
  getLoadingMetersMeasurement,
  getLoadingMetersUnit,
} from 'global/helpers/units'
import { type ReactElement, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Shipment } from 'screens/QuoteDetails/QuoteDetails.types'

export interface QuoteCargoProps {
  shipment: Shipment
  sx?: SxProps
}

const QuoteCargo = ({ shipment, sx }: QuoteCargoProps): ReactElement => {
  const { t } = useTranslation()
  const [guestUserState, _guestUserDispatch] = useContext(GuestUserContext)
  const [numberOfPackages, setNumberOfPackages] = useState<number>()
  const [grossWeight, setGrossWeight] = useState<string>()
  const [grossWeightUnit, setGrossWeightUnit] = useState<string>()
  const [volume, setVolume] = useState<string>()
  const [volumeUnit, setVolumeUnit] = useState<string>()
  const [loadingMeters, setLoadingMeters] = useState<string>()
  const [loadingMetersUnit, setLoadingMetersUnit] = useState<string>()

  useEffect(() => {
    setNumberOfPackages(shipment.cargoItems.reduce((total, item) => total + item.numberOfPackages, 0))
    const grossWeight = shipment.cargoItems.reduce((total, item) => total + item.grossWeight, 0)
    setGrossWeight(getWeightMeasurement(grossWeight, guestUserState.measurementsSystem))
    setGrossWeightUnit(getWeightUnit(guestUserState.measurementsSystem))
    const volume = shipment.cargoItems.reduce((total, item) => total + (item.volume ?? 0), 0)
    setVolume(getVolumeMeasurement(volume, guestUserState.measurementsSystem))
    setVolumeUnit(getVolumeUnit(guestUserState.measurementsSystem))
    const loadingMeters = shipment.cargoItems.reduce((total, item) => total + (item.loadingMeters ?? 0), 0)
    setLoadingMeters(getLoadingMetersMeasurement(loadingMeters, guestUserState.measurementsSystem))
    setLoadingMetersUnit(getLoadingMetersUnit(guestUserState.measurementsSystem))
  }, [guestUserState.measurementsSystem])

  return (
    <Stack direction="row" alignItems="start" sx={{ ...sx, flexWrap: 'wrap', columnGap: 1, rowGap: 0.5 }}>
      <KNAttribute
        value={numberOfPackages}
        suffix={t('screens.cs.trip_details.card.items_count', { count: numberOfPackages })}
        icon={<Inventory2OutlinedIcon />}
      />
      <KNAttribute
        value={grossWeight}
        suffix={grossWeightUnit}
        icon={<KNWeightIcon />}
      />
      <KNAttribute
        value={volume}
        suffix={volumeUnit}
        icon={<KNVolumeIcon />}
      />
      <KNAttribute
        value={loadingMeters}
        suffix={loadingMetersUnit}
        icon={<StraightenIcon />}
      />
    </Stack>
  )
}

export default QuoteCargo

